const FAIL = "Couldn't add that — try again";

const controllers = new WeakMap();

export function init(root) {
  if (root.dataset.productInit) return;
  root.dataset.productInit = '1';
  const ac = new AbortController();
  const { signal } = ac;
  controllers.set(root, ac);
  const form = root.querySelector('[data-product-form]');
  if (!form) return;
  const variants = JSON.parse(root.querySelector('[data-variants]').textContent);
  const idInput = form.querySelector('[data-id]');
  const btn = form.querySelector('[data-add]');
  const priceBox = root.querySelector('[data-price]');
  const moneyFormat = root.dataset.moneyFormat || '${{amount}}';
  const money = (cents) => {
    const group = (n, sep) => n.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
    const d = (cents / 100).toFixed(2);
    const [whole, frac] = d.split('.');
    return moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/, (m, k) => {
      if (k === 'amount') return group(whole, ',') + '.' + frac;
      if (k === 'amount_no_decimals') return group(whole, ',');
      if (k === 'amount_with_comma_separator') return group(whole, '.') + ',' + frac;
      if (k === 'amount_no_decimals_with_comma_separator') return group(whole, '.');
      return d;
    });
  };

  let current = variants.find((v) => String(v.id) === idInput.value) || variants[0];
  const state = current.options.slice();

  const find = (opts) => variants.find((v) => v.options.every((o, i) => o === opts[i]));

  function renderPrice(v) {
    if (!priceBox) return;
    priceBox.textContent = '';
    if (v.compare_at > v.price) {
      const was = document.createElement('s');
      was.className = 'pr__was';
      was.textContent = money(v.compare_at);
      const now = document.createElement('span');
      now.className = 'pr__now pr__now--sale';
      now.textContent = money(v.price);
      priceBox.append(was, ' ', now);
    } else {
      const now = document.createElement('span');
      now.className = 'pr__now';
      now.textContent = money(v.price);
      priceBox.append(now);
    }
  }

  function renderButton(v) {
    btn.textContent = '';
    if (v.available) {
      btn.disabled = false;
      btn.append('Add to bag — ');
      const p = document.createElement('span');
      p.setAttribute('data-btn-price', '');
      p.textContent = money(v.price);
      btn.append(p);
    } else {
      btn.disabled = true;
      btn.textContent = 'Sold out';
    }
  }

  function renderOptions() {
    root.querySelectorAll('.opt').forEach((group) => {
      const idx = Number(group.dataset.option);
      const label = group.querySelector('[data-selected]');
      if (label) label.textContent = state[idx];
      group.querySelectorAll('[data-value]').forEach((b) => {
        const probe = state.slice();
        probe[idx] = b.dataset.value;
        const v = find(probe);
        const ok = !!(v && v.available);
        b.classList.toggle('out', !ok);
        if (ok) b.removeAttribute('aria-disabled');
        else b.setAttribute('aria-disabled', 'true');
        b.setAttribute('aria-pressed', String(b.dataset.value === state[idx]));
      });
    });
  }

  function select(idx, value) {
    const next = state.slice();
    next[idx] = value;
    let v = find(next);
    if (!v) {
      // Combination does not exist: keep the clicked option, take the best variant that has it.
      const pool = variants.filter((x) => x.options[idx] === value);
      v = pool.find((x) => x.available) || pool[0];
    }
    if (!v) return;
    v.options.forEach((o, i) => { state[i] = o; });
    current = v;
    idInput.value = v.id;
    renderPrice(v);
    renderButton(v);
    renderOptions();
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', v.id);
      history.replaceState(null, '', url);
    } catch (e) { /* ignore */ }
    root.dispatchEvent(new CustomEvent('variant:changed', { bubbles: true, detail: { variant: v, media: v.media } }));
  }

  root.querySelectorAll('.opt [data-value]').forEach((b) => {
    b.addEventListener('click', () => select(Number(b.dataset.index), b.dataset.value), { signal });
  });

  // Swatch colours come from swatches.json; unknown colours keep the grey ring.
  const dots = root.querySelectorAll('.sw__dot');
  if (dots.length && root.dataset.swatches) {
    fetch(root.dataset.swatches)
      .then((r) => (r.ok ? r.json() : {}))
      .catch(() => ({}))
      .then((map) => {
        dots.forEach((d) => {
          const hex = map[d.dataset.color];
          if (hex) {
            d.querySelector('i').style.background = hex;
            d.closest('.sw__b').classList.add('is-known');
          }
        });
      });
  }

  let busy = false;
  async function add() {
    if (btn.disabled || busy) return;
    busy = true;
    const title = btn.dataset.title || 'item';
    const label = btn.innerHTML;
    btn.disabled = true;
    btn.setAttribute('aria-busy', 'true');
    try {
      let res;
      try {
        res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(idInput.value), quantity: 1 }] }),
        });
        if (!res.ok) throw new Error('add failed');
      } catch (err) {
        window.hssToast(FAIL);
        return;
      }
      let cart = null;
      try {
        const r = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        if (!r.ok) throw new Error('cart refresh failed');
        cart = await r.json();
      } catch (err) {
        cart = null;
      }
      if (cart) {
        const count = document.getElementById('cart-count');
        if (count) {
          count.textContent = cart.item_count;
          count.hidden = cart.item_count === 0;
        }
      }
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
      // The drawer announces and shows the add itself; keep the toast as the fallback when it cannot open.
      if (!(window.__hssDrawer && cart)) window.hssToast('Added ' + title + ' to bag');
    } finally {
      btn.removeAttribute('aria-busy');
      btn.innerHTML = label;
      btn.disabled = !current.available;
      busy = false;
    }
  }

  // Capture phase so apps hooking cart forms (Slide Cart) never see the submit.
  const intercept = (e) => {
    e.preventDefault();
    e.stopImmediatePropagation();
    add();
  };
  window.addEventListener('submit', (e) => { if (e.target === form) intercept(e); }, { capture: true, signal });
  btn.addEventListener('click', intercept, { capture: true, signal });

  renderOptions();
}

const SEL = '[data-product]';
document.querySelectorAll(SEL).forEach(init);
document.addEventListener('shopify:section:load', (e) => e.target.querySelectorAll(SEL).forEach(init));
document.addEventListener('shopify:section:unload', (e) => {
  e.target.querySelectorAll(SEL).forEach((r) => { const c = controllers.get(r); if (c) c.abort(); });
});
